import { Tooltip } from "@material-ui/core";
import { RootStatus, AdminSource } from "@authory-packages/core";
import SyncIcon from "@material-ui/icons/Sync";
import SaveIcon from "@material-ui/icons/Save";
import CheckIcon from "@material-ui/icons/Check";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import SkipNextIcon from "@material-ui/icons/SkipNext";
import ChildFriendlyIcon from "@material-ui/icons/ChildFriendly";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import CloseIcon from "@material-ui/icons/Close";
import DirectionsIcon from "@material-ui/icons/Directions";
import DeleteIcon from "@material-ui/icons/Delete";
import { Api } from "../../lib/api";
import { IconStyles, FieldSetStyles } from "../../styles";
import { useSnackbar } from "notistack";
import { goToAnchor } from "../../utils/scrollToSource";
import { SourceWithColorAndParent } from "./SourceManager";
import { useState } from "react";

type SourceActionsProps = {
  source: SourceWithColorAndParent;
  parentSource?: AdminSource;
  updateSourcesCallback: (canonicalSlug: string) => void;
  onChildCreateClick: () => void;
  onMoveUrlClick: () => void;
  onDeleteClick: () => void;
  onSaveClick: () => void;
  userSlug: string;
};

const allowDeleteIcon = [RootStatus.New];

const allowedPrimaryActionsStates = [RootStatus.New];

const suspendSourcesStates = [
  RootStatus.New,
  RootStatus.ImportFinished,
  RootStatus.ContinousCrawlRunning,
  RootStatus.AutoDisabled,
];

const allowedCloseIconsStates = [
  RootStatus.ImportFinished,
  RootStatus.New,
  RootStatus.FullCrawlAnalyzeRunning,
  RootStatus.FullCrawlRunning,
  RootStatus.AwaitingFeedback,
];

const allowedRestartCrawlStates = [
  RootStatus.ImportFinished,
  RootStatus.AwaitingFeedback,
];

const allowedActivateSourceStates = [
  RootStatus.ImportFinished,
  RootStatus.AwaitingFeedback,
];

export const SourceActions = ({
  source,
  updateSourcesCallback,
  userSlug,
  onChildCreateClick,
  onMoveUrlClick,
  onSaveClick,
  onDeleteClick,
}: SourceActionsProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const iconClasses = IconStyles();
  const fieldSetStyles = FieldSetStyles();
  const [sourceclearing, setClearing] = useState(false);

  // Inactive websites are determined by the active property and not just the root status. This needs to be reviewed and unified with backend
  const isInactive =
    source.stage === RootStatus.ContinousCrawlRunning && !source.active;
  const isSourceTuning = source.stage === RootStatus.SourceTuning;

  return (
    <fieldset className={fieldSetStyles.container}>
      <legend>Actions</legend>
      <div className={iconClasses.panelActionsSpaceLastItem}>
        {allowedPrimaryActionsStates.includes(source.stage) && (
          <>
            <Tooltip title="Start crawl">
              <PlayArrowIcon
                className={iconClasses.root}
                color="action"
                onClick={async () => {
                  try {
                    await Api.startCrawlSource(userSlug, source.canonicalSlug);
                    enqueueSnackbar("Crawl started!", { variant: "success" });
                    updateSourcesCallback(source.canonicalSlug);
                  } catch (error: any) {
                    // variant could be success, error, warning, info, or default
                    enqueueSnackbar(error.response.data.message, {
                      variant: "error",
                    });
                  }
                }}
              />
            </Tooltip>
            <Tooltip title="Skip crawl">
              <SkipNextIcon
                className={iconClasses.root}
                color="action"
                onClick={async () => {
                  try {
                    await Api.skipCrawl(userSlug, source.canonicalSlug);
                    enqueueSnackbar("Source crawl skipped!", {
                      variant: "success",
                    });
                    updateSourcesCallback(source.canonicalSlug);
                  } catch (error: any) {
                    // variant could be success, error, warning, info, or default
                    enqueueSnackbar(error.response.data.message, {
                      variant: "error",
                    });
                  }
                }}
              />
            </Tooltip>
          </>
        )}
        {suspendSourcesStates.includes(source.stage) && (
          <Tooltip title="Suspend">
            <HelpOutlineIcon
              className={iconClasses.root}
              color="action"
              onClick={async () => {
                try {
                  await Api.suspendSource(userSlug, source.canonicalSlug);
                  enqueueSnackbar("Source Suspended!", { variant: "success" });
                  updateSourcesCallback(source.canonicalSlug);
                } catch (error: any) {
                  // variant could be success, error, warning, info, or default
                  enqueueSnackbar(error?.response?.data?.message, {
                    variant: "error",
                  });
                }
              }}
            />
          </Tooltip>
        )}
        {(allowedRestartCrawlStates.includes(source.stage) || isInactive) && (
          <Tooltip title="Restart crawl">
            <SyncIcon
              className={iconClasses.root}
              color="action"
              onClick={async () => {
                try {
                  await Api.startCrawlSource(userSlug, source.canonicalSlug);
                  enqueueSnackbar("Crawl restarted!", { variant: "success" });
                  updateSourcesCallback(source.canonicalSlug);
                } catch (error: any) {
                  // variant could be success, error, warning, info, or default
                  enqueueSnackbar(error.response.data.message, {
                    variant: "error",
                  });
                }
              }}
            />
          </Tooltip>
        )}
        {(allowedCloseIconsStates.includes(source.stage) || isInactive) && (
          <Tooltip title="Clear">
            <CloseIcon
              className={!sourceclearing ? iconClasses.root : undefined}
              color={sourceclearing ? "disabled" : "action"}
              onClick={async () => {
                if (sourceclearing) return;
                try {
                  setClearing(true);
                  await Api.clearCrawl(userSlug, source.canonicalSlug);
                  enqueueSnackbar("Source cleared!", { variant: "success" });
                  updateSourcesCallback(source.canonicalSlug);
                  setClearing(false);
                } catch (error: any) {
                  // variant could be success, error, warning, info, or default
                  enqueueSnackbar(error.response.data.message, {
                    variant: "error",
                  });
                  setClearing(false);
                }
              }}
            />
          </Tooltip>
        )}
        {!!source.parentCanonicalSlug?.length ? (
          <Tooltip title="Scroll to parent website">
            <SupervisorAccountIcon
              className={iconClasses.root}
              color="action"
              onClick={() => {
                if (source.parentSource) goToAnchor(source.parentSource);
              }}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Create child website">
            <ChildFriendlyIcon
              className={iconClasses.root}
              color="action"
              onClick={onChildCreateClick}
            />
          </Tooltip>
        )}
        {!allowedPrimaryActionsStates.includes(source.stage) && (
          <Tooltip title="Move Urls">
            <DirectionsIcon
              className={iconClasses.root}
              color="action"
              onClick={onMoveUrlClick}
            />
          </Tooltip>
        )}
        {(allowDeleteIcon.includes(source.stage) || isInactive) && (
          <Tooltip title="Delete source">
            <DeleteIcon
              className={iconClasses.root}
              color="action"
              onClick={onDeleteClick}
            />
          </Tooltip>
        )}
        {!isSourceTuning && (
          <Tooltip title="Save Settings">
            <SaveIcon
              className={iconClasses.root}
              color="action"
              onClick={onSaveClick}
            />
          </Tooltip>
        )}

        {allowedActivateSourceStates.includes(source.stage) && (
          <Tooltip title="Activate">
            <CheckIcon
              className={iconClasses.root}
              color="action"
              onClick={async () => {
                try {
                  await Api.activateSource(userSlug, source.canonicalSlug);
                  enqueueSnackbar("Source activated!", { variant: "success" });
                  updateSourcesCallback(source.canonicalSlug);
                } catch (error: any) {
                  // variant could be success, error, warning, info, or default
                  enqueueSnackbar(error.response.data.message, {
                    variant: "error",
                  });
                }
              }}
            />
          </Tooltip>
        )}
      </div>
    </fieldset>
  );
};
